/**
 * Eine Liste von typisierten Intervallen.
 * Der Typ ist eine Zahl. Intervalle höheren Typs liegen über Intervallen niedrigeren Typs.
 */
export default class PriorityIntervals {

    list = [];

    constructor() {
        this.add(Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY);
    }

    /**
     * fügt ein Intervall ein
     * @param from
     * @param to
     * @param type
     * @returns {PriorityIntervals}
     */
    add(from: number, to: number, type: number) {
        this.list.push([Math.min(from, to), Math.max(from, to), type]);
        return this;
    }

    /**
     * sortiert und klopft alle Intervalle flach - höherwertige verdecken niederwertige
     * @returns {PriorityIntervals}
     */
    partition() {
        const partitions = [];
        let current = null;
        this.getSortedPoints().reduce((l, r) => {

            const t = this.list
                .filter(i => i[0] <= l && r <= i[1])
                .reduce((max, i) => Math.max(max, i[2]), Number.NEGATIVE_INFINITY);

            if (current === null || current[2] !== t) {
                current = [l, r, t];
                partitions.push(current);
            } else {
                current[1] = r;
            }

            return r;
        });

        this.list = partitions;
        return this;
    }

    /**
     * Gibt eine sortierte Liste aller Intervallgrenzen zurück
     * @returns {number[]}
     */
    getSortedPoints() {
        const points = new Set();
        for (let i of this.list) {
            points.add(i[0]);
            points.add(i[1]);
        }
        return Array.from(points).sort((a, b) => a - b);
    }

    /**
     * verschiebt die Intervalle so, dass das erste Intervall auf 0 beginnt
     * @returns {PriorityIntervals}
     */
    normalize() {
        this.finite();
        this.translate(-this.left);
        return this;
    }

    /**
     * addiert delta auf alle Intervallgrenzen
     * @param delta
     * @returns {PriorityIntervals}
     */
    translate(delta) {
        this.list.forEach(i => {
            i[0] += delta;
            i[1] += delta;
        });
        return this;
    }

    /**
     * entfernt alle Intervalle des Typs oo
     * @returns {PriorityIntervals}
     */
    finite() {
        this.list = this.list.filter(i => Number.isFinite(i[2]));
        return this;
    }

    /**
     * Linkeste Intervallgrenze
     */
    get left() {
        return this.list.reduce((min, i) => Math.min(min, Math.min(i[0], i[1])), Number.POSITIVE_INFINITY);
    }

    /**
     * Rechteste Intervallgrenze
     */
    get right() {
        return this.list.reduce((max, i) => Math.max(max, Math.max(i[0], i[1])), Number.NEGATIVE_INFINITY);
    }

    get size() {
        return this.right - this.left;
    }

    get lastIndex() {
        return this.list.length - 1;
    }

    /**
     * erzeugt ein Interval ausgehend vom Start-Interval-Index rückwärts, solange condition true ist.
     */
    backwardInterval(intervalIndex, condition): ?number[] {
        if (intervalIndex < 0 || this.list.length <= intervalIndex) {
            return null;
        }
        const right = this.list[intervalIndex][1];
        let left = null;
        for (; intervalIndex >= 0 && condition(this.list[intervalIndex]); intervalIndex--) {
            left = this.list[intervalIndex][0];
        }
        if (left === null) {
            return null;
        }
        return [left, right];
    }

    /**
     * erzeugt ein Interval ausgehend vom Start-Interval-Index vorwärts, solange condition true ist
     */
    forwardInterval(intervalIndex, condition): ?number[] {
        if (intervalIndex < 0 || this.list.length <= intervalIndex) {
            return null;
        }
        const left = this.list[intervalIndex][0];
        let right = null;
        for (; intervalIndex < this.list.length && condition(this.list[intervalIndex]); intervalIndex++) {
            right = this.list[intervalIndex][1];
        }
        if (right === null) {
            return null;
        }
        return [left, right];
    }


    static intervalSize(interval) {
        return interval == null ? 0 : interval[1] - interval[0];
    }
}

window.PriorityIntervals = PriorityIntervals;
